/* eslint-disable */
// =====================================================================
// GLOBAL AI ASİSTAN — A23
// ---------------------------------------------------------------------
// Her sayfada görünen yüzen sohbet asistanı (MasterLayout'a monte).
// Backend: /v1/assistant/message (araç orkestrasyonu: müşteri arama +
// Text-to-SQL veri sorgusu), /v1/assistant/history (yürüyen sohbet).
// Asistan sayfa da önerebilir (navigate) — "Sayfaya git" butonu çıkar.
// Erişim: ROLE_ADMIN (backend de aynı kapıyı zorlar).
// =====================================================================
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import usePermission from 'src/app/permissions/use-permission';
import AiThinkingLoader from 'src/app/components/AiThinkingLoader';
import { useAppSelector } from 'src/services/redux/app/store';

const API = `${process.env.REACT_APP_API_URL}`;

interface AssistantMessage {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  meta?: { navigate?: string | null; tools?: { tool: string }[] } | null;
  created_at?: string;
  pending?: boolean;
}

// Öneri havuzu — her panel açılışında 3 tanesi rastgele seçilir. Tümü kişisel:
// backend hem asistanda hem NLQ motorunda soruyu soranın satışçı adını ve
// kullanıcı id'sini bildiği için "portföyüm/müşterilerim/tekliflerim" doğru çözülür.
const buildSuggestionPool = (): string[] => {
  const ay = new Date().toLocaleDateString('tr-TR', { month: 'long' });
  return [
    `Portföyümde soğumaya başlayan müşteriler kimler? Önce en riskliler`,
    `Aksesuar hiç almamış cihaz müşterilerimden 5 tanesini söyle`,
    `Uzun süredir alım yapmayan büyük müşterilerim hangileri?`,
    `Bu ay portföyümden ne kadar ciro geldi, geçen aya göre nasıl?`,
    `${ay} ayında müşterilerim en çok hangi modelleri aldı?`,
    `Cevapsız bekleyen tekliflerim var mı?`,
    `En çok ciro getiren 5 müşterim kim?`,
    `Açık satış fırsatlarım neler, hangisi en eski?`,
    `Bu hafta öncelikle hangi müşterilerimi aramalıyım?`,
    `Bu yıl kaç cihaz sattım, en çok hangi gruptan?`,
  ];
};

const pickRandom = (arr: string[], n: number): string[] =>
  [...arr].sort(() => Math.random() - 0.5).slice(0, n);

const GlobalAssistant: FC = () => {
  const [, isAdmin] = usePermission('ROLE_ADMIN');
  const history = useHistory();
  const { user } = useAppSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Her panel açılışında taze 3 öneri
  const suggestions = useMemo(() => (open ? pickRandom(buildSuggestionPool(), 3) : []), [open]);

  useEffect(() => {
    if (open && !historyLoaded) {
      axios
        .get<AssistantMessage[]>(`${API}/assistant/history`)
        .then((res) => setMessages(res.data || []))
        .catch(() => {})
        .finally(() => setHistoryLoaded(true));
    }
  }, [open, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  if (!isAdmin) return null;

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assistant/message`, { message: q }, { timeout: 180000 });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data.reply, meta: { navigate: res.data.navigate, tools: res.data.tools } },
      ]);
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${err?.response?.data?.message || 'Cevap alınamadı — lütfen tekrar deneyin.'}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    try {
      await axios.delete(`${API}/assistant/history`);
      setMessages([]);
    } catch {
      /* yoksay */
    }
  };

  return (
    <>
      {/* Yüzen buton — destek butonunun üstüne istiflenir */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="AY Asistan (AI)"
          style={{
            position: 'fixed',
            bottom: 164,
            right: 22,
            zIndex: 1051,
            width: 52,
            height: 52,
            borderRadius: '50%',
            border: 'none',
            background: 'linear-gradient(135deg, #7239ea 0%, #3e97ff 100%)',
            color: 'white',
            fontSize: 22,
            boxShadow: '0 4px 14px rgba(114, 57, 234, 0.4)',
            cursor: 'pointer',
          }}
        >
          ✨
        </button>
      )}

      {/* Sohbet paneli */}
      {open && (
        <div
          className="card shadow-lg d-flex flex-column"
          style={{
            position: 'fixed',
            bottom: 20,
            right: 22,
            zIndex: 1052,
            width: 'min(420px, calc(100vw - 44px))',
            height: 'min(620px, calc(100vh - 120px))',
          }}
        >
          {/* Başlık */}
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3 rounded-top"
            style={{ background: 'linear-gradient(135deg, #7239ea 0%, #3e97ff 100%)' }}
          >
            <div className="d-flex align-items-center text-white">
              <span className="fs-3 me-2">✨</span>
              <div>
                <div className="fw-bold">AY Asistan</div>
                <div className="fs-8 opacity-75">Müşteri arama · veri sorgusu · yönlendirme</div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-icon btn-active-light-primary text-white" onClick={clear} title="Sohbeti temizle">
                🗑
              </button>
              <button className="btn btn-sm btn-icon btn-active-light-primary text-white" onClick={() => setOpen(false)} title="Kapat">
                ✕
              </button>
            </div>
          </div>

          {/* Mesajlar */}
          <div ref={scrollRef} className="flex-grow-1 overflow-auto px-4 py-3 bg-light">
            {messages.length === 0 && !loading && (
              <div className="text-center py-6">
                <div className="fs-2 mb-2">👋</div>
                <div className="text-gray-700 fw-semibold mb-1">
                  Merhaba{user?.name ? ` ${user.name}` : ''}! Ben AY Asistan.
                </div>
                <div className="text-gray-500 fs-7 mb-4">
                  Müşterilerinizi sorabilir, satış verisinde soru sorabilir, sizi doğru sayfaya götürebilirim.
                </div>
                <div className="d-flex flex-column gap-2 align-items-center">
                  {suggestions.map((s) => (
                    <button key={s} className="btn btn-sm btn-light-primary py-1" onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id ?? `local-${i}`} className={`d-flex mb-3 ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                <div
                  className={`rounded p-3 fs-7 ${m.role === 'user' ? 'bg-primary text-white' : 'bg-white border'}`}
                  style={{ maxWidth: '85%' }}
                >
                  {m.role === 'assistant' ? (
                    <>
                      <div className="assistant-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      </div>
                      {m.meta?.navigate && (
                        <button
                          className="btn btn-sm btn-light-primary py-1 px-3 mt-1"
                          onClick={() => {
                            history.push(m.meta!.navigate!);
                            setOpen(false);
                          }}
                        >
                          → Sayfaya git
                        </button>
                      )}
                    </>
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="d-flex justify-content-start mb-3">
                <div className="bg-white border rounded p-3">
                  <AiThinkingLoader compact />
                </div>
              </div>
            )}
          </div>

          {/* Girdi */}
          <div className="d-flex gap-2 p-3 border-top">
            <textarea
              className="form-control form-control-sm"
              rows={1}
              placeholder="Sorunuzu yazın…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              disabled={loading}
              style={{ resize: 'none' }}
            />
            <button className="btn btn-sm btn-primary flex-shrink-0" onClick={() => send(input)} disabled={loading || !input.trim()}>
              Gönder
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalAssistant;
